import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { TypeAgenda } from '@/lib/types';

export type InterventionTemplate = {
  id: string;
  nom: string;
  typeAgenda: TypeAgenda;
  dureeEstimeeMinutes: number;
  nature: string;
  descriptionDefault: string | null;
  actif: boolean;
};

export type CreateTemplateInput = {
  nom: string;
  typeAgenda: TypeAgenda;
  dureeEstimeeMinutes: number;
  nature: string;
  descriptionDefault?: string;
  actif?: boolean;
};

export type UpdateTemplateInput = Partial<CreateTemplateInput>;

/**
 * Liste les templates d'intervention (filtrés par typeAgenda + actif si fourni).
 * Si typeAgenda est défini, on ne récupère que les templates actifs pour ce type.
 */
export function useTemplates(typeAgenda?: TypeAgenda, onlyActive = true) {
  const params = new URLSearchParams();
  if (typeAgenda) params.set('typeAgenda', typeAgenda);
  if (onlyActive) params.set('actif', 'true');
  const qs = params.toString();

  return useQuery({
    queryKey: ['admin', 'templates', { typeAgenda, onlyActive }],
    queryFn: () =>
      api.get<{ templates: InterventionTemplate[] }>(
        `/admin/templates${qs ? `?${qs}` : ''}`,
      ),
    select: (d) => d.templates,
    staleTime: 60_000,
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTemplateInput) =>
      api.post<{ template: InterventionTemplate }>('/admin/templates', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'templates'] }),
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTemplateInput }) =>
      api.patch<{ template: InterventionTemplate }>(`/admin/templates/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'templates'] }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/admin/templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'templates'] }),
  });
}
